import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { trackEvent } from '@/lib/analytics';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Check, Sparkles, TrendingUp, Zap, Flame, Ticket, BadgePercent, X } from 'lucide-react';
import SEOHead from '@/components/marketplace/SEOHead';
import GstDetailsDialog from '@/components/marketplace/GstDetailsDialog';
import { toast } from 'sonner';

const iconFor = (kind) => kind === 'subscription' ? Sparkles : kind === 'top' ? TrendingUp : kind === 'urgent' ? Flame : Zap;

// #8: Round rupee display — no decimals, ever. B2B pricing is always in
// whole rupees; the trailing ".00" made cards look noisier without adding
// any information.
const formatINR = (v) => `₹ ${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0, minimumFractionDigits: 0 })}`;

// #9: Reusable savings chip — appears on every card that has a real
// discount vs its list price, so users can see the exact rupee amount they
// save at a glance (subscription, boost, ad-pack — all use it now).
const SavingsChip = ({ amount, promoName, testId }) => (
  <div
    className='text-[11px] mt-1 inline-flex items-center gap-1 rounded-full bg-[hsl(var(--verified))]/10 text-[hsl(var(--verified))] px-2 py-0.5 font-medium'
    data-testid={testId}
  >
    <BadgePercent className='h-3 w-3' /> {promoName ? `${promoName}: ` : ''}You save {formatINR(amount)}
  </div>
);

const PriceBlock = ({ plan, autoPriced, promoLoaded }) => {
  if (plan.price_inr === 0) {
    return (
      <div className='mt-3'>
        <div className='text-3xl font-semibold tracking-tight tabular-nums'>Free</div>
      </div>
    );
  }
  const showDurationHint = plan.kind === 'subscription' && plan.duration_days > 0;
  // #7: Business (and any promo-eligible plan) briefly showed the LIST
  // price before the async /promotions/validate call resolved and swapped
  // it for the discounted one. Show a thin skeleton until the promo lookup
  // has returned so the user never sees an incorrect price. Once loaded we
  // render the real block synchronously.
  if (plan.price_inr > 0 && !promoLoaded) {
    return (
      <div className='mt-3' data-testid={`plan-price-skeleton-${plan.slug}`}>
        <div className='h-9 w-32 rounded bg-secondary/60 animate-pulse' />
        {showDurationHint && <div className='mt-1 h-3 w-20 rounded bg-secondary/40 animate-pulse' />}
      </div>
    );
  }
  if (autoPriced?.ok && autoPriced.discount_inr > 0) {
    return (
      <div className='mt-3'>
        <div className='flex items-baseline gap-2 flex-wrap'>
          <div className='text-3xl font-semibold tracking-tight tabular-nums'>{formatINR(autoPriced.final_amount_inr)}</div>
          <div className='text-lg text-muted-foreground line-through tabular-nums'>{formatINR(plan.price_inr)}</div>
        </div>
        <SavingsChip amount={autoPriced.discount_inr} promoName={autoPriced.promotion?.name} testId={`plan-discount-${plan.slug}`} />
        {showDurationHint && <div className='text-xs text-muted-foreground mt-1'>per {plan.duration_days === 30 ? 'month' : `${plan.duration_days} days`}</div>}
        <div className='text-[11px] text-muted-foreground mt-0.5' data-testid={`plan-gst-note-${plan.slug}`}>+ GST extra as applicable</div>
      </div>
    );
  }
  return (
    <div className='mt-3'>
      <div className='text-3xl font-semibold tracking-tight tabular-nums'>{formatINR(plan.price_inr)}</div>
      {showDurationHint && <div className='text-xs text-muted-foreground'>per {plan.duration_days === 30 ? 'month' : `${plan.duration_days} days`}</div>}
      <div className='text-[11px] text-muted-foreground mt-0.5' data-testid={`plan-gst-note-${plan.slug}`}>+ GST extra as applicable</div>
    </div>
  );
};

const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [autoPrices, setAutoPrices] = useState({}); // plan.id -> validate result (no code)
  // #7: Track when the auto-price promo lookup has completed for the
  // current plans list so PriceBlock can render a skeleton until real
  // pricing arrives, avoiding the list-price flicker.
  const [promoLoaded, setPromoLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkoutBusy, setCheckoutBusy] = useState('');
  const [coupon, setCoupon] = useState('');
  const [couponApplying, setCouponApplying] = useState(false);
  const [appliedCouponForPlan, setAppliedCouponForPlan] = useState(null); // { plan_id, result }
  // Current active subscription — used to warn a user before they buy
  // another plan while an existing one is still valid. Null while loading.
  const [currentSub, setCurrentSub] = useState(null);
  const [expiredSub, setExpiredSub] = useState(null);
  const [activePlanPrompt, setActivePlanPrompt] = useState({ open: false, plan: null });
  // GST details capture — shown as an additive step before Razorpay
  // checkout so the invoice we generate later carries the buyer's business
  // details. Skippable — non-GST purchases keep working exactly as today.
  const [gstDialog, setGstDialog] = useState({ open: false, plan: null });
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/payments/plans').then(r => setPlans(r.data)).finally(() => setLoading(false));
    // Analytics: fire once when the pricing page loads so we can measure
    // top-of-checkout-funnel intent independent of route pageviews.
    trackEvent('plan_page_view', { source: 'pricing' });
  }, []);

  // Fetch current active subscription (only when a user is signed in). Runs
  // once on mount + after each successful purchase so the warning stays
  // accurate.
  useEffect(() => {
    if (!user) { setCurrentSub(null); setExpiredSub(null); return; }
    api.get('/payments/subscription/status')
      .then(r => { setCurrentSub(r.data?.active || null); setExpiredSub(r.data?.expired || null); })
      .catch(() => { setCurrentSub(null); setExpiredSub(null); });
  }, [user]);

  // Precompute auto-offer pricing per plan
  useEffect(() => {
    const load = async () => {
      setPromoLoaded(false);
      const promises = plans.filter(p => p.price_inr > 0).map(async p => {
        try {
          const { data } = await api.post('/promotions/validate', { plan_id: p.id });
          return [p.id, data];
        } catch { return [p.id, null]; }
      });
      const results = await Promise.all(promises);
      setAutoPrices(Object.fromEntries(results));
      setPromoLoaded(true);
    };
    if (plans.length) load();
  }, [plans]);

  const subs = plans.filter(p => p.kind === 'subscription');
  const boosts = plans.filter(p => !['subscription', 'ad_pack'].includes(p.kind));

  const applyCoupon = async (plan) => {
    const c = coupon.trim().toUpperCase();
    if (!c) return;
    setCouponApplying(true);
    try {
      const { data } = await api.post('/promotions/validate', { plan_id: plan.id, code: c });
      if (!data.ok) {
        toast.error('Invalid or expired coupon');
        setAppliedCouponForPlan(null);
        return;
      }
      setAppliedCouponForPlan({ plan_id: plan.id, result: data });
      toast.success(`Coupon applied! You save ${formatINR(data.discount_inr)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to validate coupon');
    } finally { setCouponApplying(false); }
  };

  const startCheckout = async (plan, methodOverride = null) => {
    if (!user) { navigate('/login', { state: { from: '/pricing' } }); return; }
    if (plan.price_inr === 0) { toast.info('You are already on Free tier'); return; }
    if (!['subscription', 'ad_pack'].includes(plan.kind)) {
      toast.info('Open a listing to apply a Featured/Top/Urgent boost.');
      return;
    }
    // If the user already has an active paid subscription, show a
    // confirmation prompt — buying another plan doesn't stack, so they
    // should know before spending money.
    if (plan.kind === 'subscription' && currentSub && currentSub.active && currentSub.expires_at) {
      const expiresIso = currentSub.expires_at;
      const nowMs = Date.now();
      const expiresMs = new Date(expiresIso).getTime();
      if (expiresMs > nowMs) {
        setActivePlanPrompt({ open: true, plan, methodOverride });
        return;
      }
    }
    return _openGstDialog(plan, methodOverride);
  };

  // Show GST details capture before actually creating the order. Only for
  // paid subscription/ad-pack purchases where an invoice will be generated.
  const _openGstDialog = (plan, methodOverride = null) => {
    setGstDialog({ open: true, plan, methodOverride });
  };

  const _doCheckout = async (plan, gstDetails = null, methodOverride = null) => {
    setCheckoutBusy(plan.id);
    try {
      const cfg = (await api.get('/payments/config')).data;
      const appliedCoupon = appliedCouponForPlan?.plan_id === plan.id ? coupon.trim().toUpperCase() : null;
      const method = methodOverride || (cfg.razorpay_enabled ? 'razorpay' : 'manual');
      const orderPayload = { plan_id: plan.id, method };
      if (appliedCoupon) orderPayload.coupon_code = appliedCoupon;
      if (gstDetails) orderPayload.gst_details = gstDetails;
      const order = (await api.post('/payments/create-order', orderPayload)).data;
      // Analytics: checkout_started fires as soon as an order exists,
      // regardless of gateway path. `value` is in INR.
      trackEvent('checkout_started', {
        plan_id: plan.id,
        plan_slug: plan.slug,
        currency: 'INR',
        value: order.amount || plan.price_inr,
        coupon: appliedCoupon || undefined,
        method: order.method,
      });

      // Points-paid orders complete synchronously (backend debits +
      // activates + generates invoice on create-order). No gateway
      // hand-off needed — jump the buyer to their invoices.
      if (order.method === 'points' && order.status === 'paid') {
        toast.success('Purchased with Rewards Points! Your plan is now active and the invoice is ready.');
        trackEvent('payment_success', {
          plan_id: plan.id,
          plan_slug: plan.slug,
          currency: 'INR',
          value: order.amount || plan.price_inr,
          transaction_id: order.id,
          method: 'points',
        });
        navigate('/dashboard/invoices');
        return;
      }

      if (order.method === 'razorpay' && order.razorpay_order_id && cfg.razorpay_key_id) {
        if (!window.Razorpay) {
          await new Promise((resolve, reject) => {
            const s = document.createElement('script');
            s.src = 'https://checkout.razorpay.com/v1/checkout.js';
            s.onload = resolve; s.onerror = reject;
            document.body.appendChild(s);
          });
        }
        const opts = {
          key: cfg.razorpay_key_id,
          amount: Math.round(order.amount * 100),
          currency: 'INR',
          name: 'IndusFact B2B Solutions',
          description: order.description + (order.promotion_name ? ` (${order.promotion_name})` : ''),
          order_id: order.razorpay_order_id,
          prefill: { name: user.name, email: user.email, contact: user.mobile || '' },
          theme: { color: '#0d3b7a' },
          handler: async (response) => {
            try {
              await api.post('/payments/verify', {
                order_id: order.id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              });
              toast.success('Payment verified! Your plan is now active.');
              // Analytics: payment_success \u2014 the single most valuable
              // revenue event for the platform.
              trackEvent('payment_success', {
                plan_id: plan.id,
                plan_slug: plan.slug,
                currency: 'INR',
                value: order.amount || plan.price_inr,
                transaction_id: response.razorpay_payment_id,
                method: 'razorpay',
              });
              navigate('/dashboard/billing');
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Verification failed');
            }
          },
        };
        const rzp = new window.Razorpay(opts);
        rzp.open();
      } else {
        toast.success('Order created. Please complete payment via bank/UPI and submit the reference in Billing.');
        navigate('/dashboard/billing');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
    } finally { setCheckoutBusy(''); }
  };

  return (
    <div className='indus-container py-10'>
      <SEOHead
        title='Pricing · IndusFact B2B Solutions'
        description='Free listings, Featured and Top boosts, and subscription plans for industrial B2B sellers on IndusFact.'
        keywords='IndusFact pricing, industrial listing plans, featured listings, subscription'
      />
      <div className='text-center max-w-2xl mx-auto'>
        <h1 className='text-3xl sm:text-4xl font-semibold tracking-tight'>Simple, transparent pricing</h1>
        <p className='text-muted-foreground mt-2'>Start free. Upgrade when you're ready to reach more buyers.</p>
      </div>

      {expiredSub && !currentSub && (
        <div
          className='mt-6 mx-auto max-w-2xl rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 px-4 py-3 text-sm'
          data-testid='pricing-expired-plan-notice'
        >
          Your <strong>{expiredSub.plan_name || 'previous'}</strong> plan expired on{' '}
          {new Date(expiredSub.expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}.
          You are currently on the Free plan — renew or pick a plan below to restore paid benefits for new listings.
        </div>
      )}

      {loading ? (
        <div className='py-10 text-center text-muted-foreground'>Loading plans…</div>
      ) : (
        <>
          <div className='mt-10 grid md:grid-cols-3 gap-4' data-testid='pricing-subscriptions'>
            {subs.map(p => {
              const Icon = iconFor(p.kind);
              const isBusiness = p.slug === 'business';
              const applied = appliedCouponForPlan?.plan_id === p.id ? appliedCouponForPlan.result : null;
              const effectivePriced = applied || autoPrices[p.id];
              return (
                <Card key={p.id} className={`p-6 flex flex-col ${isBusiness ? 'border-primary ring-2 ring-primary/20' : ''}`} data-testid={`plan-${p.slug}`}>
                  {isBusiness && <div className='inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider text-accent mb-2'><Sparkles className='h-3 w-3' /> Most popular</div>}
                  <div className='flex items-center gap-2 mb-1'>
                    <span className='h-9 w-9 rounded-lg bg-secondary flex items-center justify-center'><Icon className='h-4 w-4 text-primary' /></span>
                    <div className='font-semibold text-lg'>{p.name}</div>
                  </div>
                  <PriceBlock plan={p} autoPriced={effectivePriced} promoLoaded={promoLoaded} />
                  <p className='text-sm text-muted-foreground mt-3'>{p.description}</p>
                  <ul className='mt-4 space-y-2 text-sm flex-1'>
                    {(p.features || []).map((f, i) => (
                      <li key={i} className='flex items-start gap-2'>
                        <Check className='h-4 w-4 text-[hsl(var(--verified))] shrink-0 mt-0.5' />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Coupon input (only on business/enterprise, not free) */}
                  {p.price_inr > 0 && (
                    <div className='mt-4'>
                      {applied ? (
                        <div className='flex items-center gap-2 rounded-lg border border-[hsl(var(--verified))]/40 bg-[hsl(var(--verified))]/10 px-3 py-1.5 text-xs'>
                          <Ticket className='h-3.5 w-3.5 text-[hsl(var(--verified))]' />
                          <span className='font-medium'>{applied.promotion.code}</span>
                          <span className='text-muted-foreground'>−{formatINR(applied.discount_inr)}</span>
                          <button
                            type='button'
                            onClick={() => { setAppliedCouponForPlan(null); setCoupon(''); }}
                            className='ml-auto text-muted-foreground hover:text-foreground'
                            data-testid={`coupon-remove-${p.slug}`}
                          >
                            <X className='h-3.5 w-3.5' />
                          </button>
                        </div>
                      ) : (
                        <div className='flex gap-1.5'>
                          <Input
                            value={coupon}
                            onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                            placeholder='Coupon code'
                            className='h-9 text-xs uppercase'
                            data-testid={`coupon-input-${p.slug}`}
                          />
                          <Button
                            size='sm'
                            variant='outline'
                            onClick={() => applyCoupon(p)}
                            disabled={couponApplying || !coupon.trim()}
                            data-testid={`coupon-apply-${p.slug}`}
                          >
                            Apply
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  <Button
                    className='mt-4 w-full'
                    variant={isBusiness ? 'default' : 'outline'}
                    onClick={() => startCheckout(p)}
                    disabled={!!checkoutBusy}
                    data-testid={`plan-cta-${p.slug}`}
                  >
                    {checkoutBusy === p.id ? 'Preparing…' : (p.price_inr === 0 ? 'Current tier' : (expiredSub && !currentSub ? `Renew with ${p.name}` : `Choose ${p.name}`))}
                  </Button>
                  {p.price_inr > 0 && (
                    <Button
                      className='mt-2 w-full text-xs'
                      variant='ghost'
                      onClick={() => startCheckout(p, 'points')}
                      disabled={!!checkoutBusy}
                      data-testid={`plan-cta-points-${p.slug}`}
                    >
                      Pay with Rewards Points
                    </Button>
                  )}
                </Card>
              );
            })}
          </div>

          <div className='mt-14'>
            <h2 className='text-2xl font-semibold tracking-tight'>Try before subscribing — Ad Packs</h2>
            <p className='text-muted-foreground text-sm'>One-time purchase, credits never expire. Each ad in a pack posts with 4 images + 1 video + 1 YouTube link and is auto-Featured for 2 days.</p>
            <div className='mt-4 grid sm:grid-cols-2 gap-4' data-testid='pricing-adpacks'>
              {plans.filter(p => p.kind === 'ad_pack').map(p => {
                const priced = autoPrices[p.id];
                const applied = appliedCouponForPlan?.plan_id === p.id ? appliedCouponForPlan.result : null;
                const effectivePriced = applied || priced;
                return (
                  <Card key={p.id} className='p-6 flex flex-col' data-testid={`plan-${p.slug}`}>
                    <div className='flex items-center gap-2 mb-1'>
                      <span className='h-9 w-9 rounded-lg bg-secondary flex items-center justify-center'><Ticket className='h-4 w-4 text-primary' /></span>
                      <div className='font-semibold text-lg'>{p.name}</div>
                      {p.slug === 'ad-pack-5' && (
                        <span className='ml-auto inline-flex items-center gap-1 rounded-full bg-accent/15 text-accent px-2 py-0.5 text-[10px] font-medium'>
                          <BadgePercent className='h-3 w-3' /> Save 20%
                        </span>
                      )}
                    </div>
                    <PriceBlock plan={p} autoPriced={effectivePriced} promoLoaded={promoLoaded} />
                    <p className='text-sm text-muted-foreground mt-3'>{p.description}</p>
                    <ul className='mt-3 space-y-1.5 text-sm flex-1'>
                      {(p.features || []).map((f, i) => (
                        <li key={i} className='flex items-start gap-2'>
                          <Check className='h-4 w-4 text-[hsl(var(--verified))] shrink-0 mt-0.5' />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      className='mt-4 w-full'
                      variant='default'
                      onClick={() => startCheckout(p)}
                      disabled={!!checkoutBusy}
                      data-testid={`plan-cta-${p.slug}`}
                    >
                      {checkoutBusy === p.id ? 'Preparing…' : `Buy ${p.name}`}
                    </Button>
                    <Button
                      className='mt-2 w-full text-xs'
                      variant='ghost'
                      onClick={() => startCheckout(p, 'points')}
                      disabled={!!checkoutBusy}
                      data-testid={`plan-cta-points-${p.slug}`}
                    >
                      Pay with Rewards Points
                    </Button>
                  </Card>
                );
              })}
            </div>
          </div>

          <div className='mt-14'>
            <h2 className='text-2xl font-semibold tracking-tight'>Boost individual listings</h2>
            <p className='text-muted-foreground text-sm'>One-time boosts to give a listing more visibility.</p>
            <div className='mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4' data-testid='pricing-boosts'>
              {boosts.map(p => {
                const Icon = iconFor(p.kind);
                const priced = autoPrices[p.id];
                return (
                  <Card key={p.id} className='p-5 flex flex-col' data-testid={`plan-${p.slug}`}>
                    <div className='flex items-center gap-2 mb-1'>
                      <span className='h-8 w-8 rounded-md bg-secondary flex items-center justify-center'><Icon className='h-4 w-4 text-accent' /></span>
                      <div className='font-semibold'>{p.name}</div>
                    </div>
                    {!promoLoaded && p.price_inr > 0 ? (
                      <div className='mt-2' data-testid={`plan-price-skeleton-${p.slug}`}>
                        <div className='h-7 w-24 rounded bg-secondary/60 animate-pulse' />
                      </div>
                    ) : priced?.ok && priced.discount_inr > 0 ? (
                      <div className='mt-2'>
                        <div className='flex items-baseline gap-2'>
                          <div className='text-2xl font-semibold tabular-nums'>{formatINR(priced.final_amount_inr)}</div>
                          <div className='text-sm text-muted-foreground line-through'>{formatINR(p.price_inr)}</div>
                        </div>
                        {/* #9: Explicit "You save ₹X" chip on Boost cards too. */}
                        <SavingsChip amount={priced.discount_inr} promoName={priced.promotion?.name} testId={`plan-savings-${p.slug}`} />
                      </div>
                    ) : (
                      <div className='text-2xl font-semibold tabular-nums mt-2'>{formatINR(p.price_inr)}</div>
                    )}
                    <div className='text-[11px] text-muted-foreground mt-0.5' data-testid={`plan-gst-note-${p.slug}`}>+ GST extra as applicable</div>
                    <p className='text-xs text-muted-foreground mt-1'>{p.description}</p>
                    <div className='mt-3 text-xs text-muted-foreground'>Apply on a listing from <Link to='/dashboard/listings' className='text-primary hover:underline'>My Listings</Link>.</div>
                  </Card>
                );
              })}
            </div>
          </div>

          <div className='mt-10 text-xs text-muted-foreground text-center'>
            Paying via Razorpay (secure) or via bank/UPI (manual approval by our team). GST invoice available on request.
          </div>
        </>
      )}

      {/* Active-plan confirmation dialog — shown when a user with a valid
          paid subscription tries to buy another plan. Prevents accidental
          double-purchases (plans do not stack). */}
      <Dialog
        open={activePlanPrompt.open}
        onOpenChange={(o) => !o && setActivePlanPrompt({ open: false, plan: null })}
      >
        <DialogContent data-testid='active-plan-warn-dialog' className='max-w-lg'>
          <DialogHeader>
            <DialogTitle>You already have an active plan</DialogTitle>
            <DialogDescription>
              Your current plan will not be extended by this purchase — the new plan starts fresh and any remaining time on your existing plan won&apos;t carry over.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-3 text-sm'>
            <div className='rounded-md border bg-secondary/40 p-3'>
              <div className='text-xs uppercase tracking-wider text-muted-foreground'>Current plan</div>
              <div className='font-semibold mt-1'>{currentSub?.plan_name || currentSub?.plan_id}</div>
              {currentSub?.expires_at && (
                <div className='text-xs text-muted-foreground mt-1'>
                  Valid until {new Date(currentSub.expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
              )}
            </div>
            <div className='rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 p-3 text-xs'>
              Purchasing another plan will <strong>not add benefits or extend</strong> your existing plan. Do you still want to proceed?
            </div>
          </div>
          <DialogFooter className='gap-2'>
            <Button variant='outline' onClick={() => setActivePlanPrompt({ open: false, plan: null })} data-testid='active-plan-warn-cancel'>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const p = activePlanPrompt.plan;
                const m = activePlanPrompt.methodOverride || null;
                setActivePlanPrompt({ open: false, plan: null, methodOverride: null });
                if (p) _openGstDialog(p, m);
              }}
              data-testid='active-plan-warn-proceed'
            >
              Proceed anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GST business-details capture — appears before Razorpay hand-off. */}
      <GstDetailsDialog
        open={gstDialog.open}
        plan={gstDialog.plan}
        method={gstDialog.methodOverride || null}
        couponCode={gstDialog.plan && appliedCouponForPlan?.plan_id === gstDialog.plan.id ? coupon.trim().toUpperCase() : null}
        onCancel={() => setGstDialog({ open: false, plan: null, methodOverride: null })}
        onConfirm={(details) => {
          const p = gstDialog.plan;
          const m = gstDialog.methodOverride || null;
          setGstDialog({ open: false, plan: null, methodOverride: null });
          if (p) _doCheckout(p, details, m);
        }}
      />
    </div>
  );
};

export default Pricing;
